'use client'

import React from 'react'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogAppCTA, BlogWhatTheAppChecks, BlogSources, BlogTLDR, BlogKeyTakeaways, BlogFAQ } from '@/app/components/blog/BlogAeo'
import AppStoreLink from '@/app/components/AppStoreLink'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics and Trying to Conceive: What Couples Need to Know"
      author="MicroPlastics Team"
      date="January 25, 2026"
      readTime="10 min read"
      heroImage="/blog/app-search.png"
      heroImageAlt="MicroPlastics app helping check product safety"
    >
      <BlogTLDR>
        The <strong>3-6 month window before conception</strong> is the
        best time for both partners to reduce microplastic and
        plasticiser exposure. Sperm regenerate every 64-72 days, so changes
        a male partner makes today affect the sperm he produces 2-3 months
        later. Egg quality is also influenced by recent (3-6 month)
        exposure. <strong>BPA and phthalate body burdens drop measurably</strong>{' '}
        in this window with consistent reduction.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Sperm regenerate every 64-72 days, male reduction has a clear biological window.</>,
          <>Egg quality is influenced by recent (3-6 month) plasticiser exposure.</>,
          <>Both partners benefit from a coordinated preconception reduction plan.</>,
          <>Top swaps: filtered water in glass, no microwaving plastic, plastic-free personal care, cotton/wool underwear for the male partner.</>,
          <>2026 (ESHRE): particles reach both partners, 69% of follicular and 55% of seminal fluid samples. PTFE (non-stick coating) led at 31%, making worn non-stick pans an easy preconception swap.</>,
        ]}
      />

      {/* Introduction */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        A Hidden Factor in the Fertility Crisis
      </h2>
      <p>
        Global fertility rates have been declining for decades, and sperm counts in
        Western nations have dropped by more than 50% since 1973 according to a
        comprehensive 2022 meta-analysis published in <em>Human Reproduction
        Update</em>. While many factors contribute to declining fertility, including
        age, stress, and lifestyle changes, a growing body of evidence points to an
        underappreciated culprit: microplastics and the endocrine-disrupting chemicals
        they carry.
      </p>
      <p>
        In 2024, researchers made a startling discovery that brought this issue into
        sharp focus. A study published in <em>Toxicological Sciences</em> detected
        microplastics in human testicular tissue for the first time, finding an
        average concentration of 329.44 micrograms per gram of tissue, nearly three
        times the concentration found in human placentas. The most common plastic
        found was polyethylene, followed by PVC. The researchers noted an inverse
        correlation between microplastic concentration and sperm count in the tissue
        samples.
      </p>
      <p>
        For couples who are trying to conceive or planning to start a family, this
        research highlights the importance of addressing microplastic exposure as part
        of preconception planning. The encouraging news is that many sources of
        exposure are modifiable, and starting a plastic reduction plan 3 to 6 months
        before trying to conceive gives your body time to clear accumulated chemicals.
      </p>

      {/* How microplastics affect female fertility */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        2026 Update: Both Partners, Measured Directly
      </h2>
      <p>
        Preconception advice has historically focused on the woman. New data presented at the{' '}
        <strong>41st ESHRE Annual Meeting</strong> is a good argument for treating it as a
        two-person project.
      </p>
      <p>
        Researchers measured microplastics in follicular fluid from 29 women and seminal
        fluid from 22 men. They found particles in <strong>69% of follicular fluid</strong>{' '}
        and <strong>55% of seminal fluid</strong> samples. The most common polymer was{' '}
        <strong>PTFE (31%)</strong>, the non-stick cookware coating, ahead of polypropylene
        (28%), PET (17%), polyamide (14%), polyethylene (10%), polyurethane (10%) and
        polystyrene (7%).
      </p>
      <p>
        That PTFE result is the one with an obvious household action attached: scratched or
        aging non-stick pans are a plausible everyday source, and replacing the worst offender
        with stainless or cast iron is a one-time, low-cost change that fits neatly into a
        preconception window.
      </p>
      <p>
        A negative correlation was observed between total follicular-fluid microplastics and
        fertilization rate. Treat that as a hypothesis, not a finding to plan around: the
        samples are small and, as the researchers stated plainly,{' '}
        <strong>current evidence does not show microplastics cause infertility</strong>. The
        case for a 3--6 month reduction window is that the changes are cheap and harmless,
        not that the science is settled.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How Microplastics Affect Female Fertility
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Endocrine Disruption and Ovarian Function
      </h3>
      <p>
        Female fertility depends on a precisely orchestrated cascade of hormones.
        Follicle-stimulating hormone (FSH) triggers egg development, luteinizing
        hormone (LH) triggers ovulation, and estrogen and progesterone prepare the
        uterine lining for implantation. Microplastics and their associated chemicals
        disrupt this hormonal balance at multiple points.
      </p>
      <p>
        BPA and phthalates are xenoestrogens, chemicals that mimic or interfere with
        estrogen signaling. Even at low doses, these chemicals can suppress FSH
        production, leading to incomplete egg maturation. They can alter the timing of
        the LH surge, leading to irregular or absent ovulation. They can reduce the
        quality of the uterine lining, making implantation less likely. And they can
        impair the function of the corpus luteum, reducing progesterone production
        needed to sustain early pregnancy.
      </p>
      <p>
        A 2023 study published in <em>Fertility and Sterility</em> found that women
        with higher urinary concentrations of phthalate metabolites had a 29% longer
        time to pregnancy compared to women with lower concentrations. Another study
        from the same year found that BPA exposure was associated with reduced ovarian
        reserve, as measured by anti-Mullerian hormone (AMH) levels, in women under 35.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Impact on Egg Quality
      </h3>
      <p>
        Beyond hormonal disruption, microplastics may directly affect egg quality. A
        2024 study using mouse models found that exposure to polystyrene nanoplastics
        caused oxidative stress in ovarian tissue, leading to increased rates of DNA
        damage in oocytes, impaired mitochondrial function in developing eggs, and
        accelerated follicular atresia (premature death of developing eggs). While
        direct human evidence is still being gathered, these findings are consistent
        with epidemiological data showing associations between plastic chemical
        exposure and reduced oocyte quality in IVF patients.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Endometriosis and Fibroids
      </h3>
      <p>
        Microplastic-associated chemicals have been linked to conditions that impair
        fertility. Phthalate exposure has been associated with increased risk of
        endometriosis, a condition where uterine tissue grows outside the uterus,
        causing pain and reducing fertility. A 2022 meta-analysis found that women
        with endometriosis had significantly higher urinary phthalate levels than
        controls. Similarly, BPA exposure has been linked to increased risk of
        uterine fibroids, which can interfere with embryo implantation and increase
        miscarriage risk.
      </p>

      {/* How microplastics affect male fertility */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How Microplastics Affect Male Fertility
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Microplastics in Testicular Tissue
      </h3>
      <p>
        The 2024 discovery of microplastics in human testes was a watershed moment for
        understanding male fertility decline. The study, conducted at the University of
        New Mexico, analyzed testicular tissue from 23 human donors and 47 dogs. Every
        single sample contained microplastics. The human samples contained
        concentrations nearly three times higher than what had been found in human
        placentas, suggesting that the testes may be particularly susceptible to
        microplastic accumulation.
      </p>
      <p>
        The researchers found 12 different types of plastic in testicular tissue, with
        polyethylene (used in plastic bags and bottles) comprising 23% of the total,
        followed by PVC at 16%. Most concerning was the finding that higher
        microplastic concentrations in testicular tissue correlated with lower sperm
        counts. While the study examined post-mortem tissue and could not assess
        fertility directly, the implications for male reproductive health are
        significant.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Sperm Quality Decline
      </h3>
      <p>
        Multiple studies have linked plastic chemical exposure to measurable declines
        in sperm parameters. A 2023 study of 1,045 men from fertility clinics found
        that those with higher urinary BPA concentrations had 23% lower sperm
        concentration, 13% lower sperm motility, and a higher proportion of
        abnormally shaped sperm compared to those with lower BPA levels. Phthalate
        exposure has been similarly associated with reduced sperm DNA integrity, with
        a 2024 study finding that men in the highest quartile of phthalate exposure
        had twice the rate of sperm DNA fragmentation compared to the lowest quartile.
      </p>
      <p>
        These effects are particularly concerning because sperm development
        (spermatogenesis) takes approximately 74 days, meaning that current plastic
        chemical exposure is affecting sperm that will be used in conception attempts
        two to three months from now. This is why starting a preconception detox plan
        at least 3 months before trying to conceive is recommended.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Testosterone and Hormonal Effects
      </h3>
      <p>
        Phthalates are potent anti-androgens that can suppress testosterone production.
        A 2023 meta-analysis of 18 studies found a significant inverse association
        between urinary phthalate metabolite levels and serum testosterone
        concentrations in adult men. Reduced testosterone not only impairs sperm
        production but can also decrease libido and sexual function, further reducing
        the chances of conception. BPA exposure has additionally been linked to
        elevated estradiol levels in men, disrupting the testosterone-to-estrogen
        ratio that is critical for healthy sperm production.
      </p>

      {/* IVF impact */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Microplastics and IVF Success Rates
      </h2>
      <p>
        For couples undergoing assisted reproductive technologies, microplastic
        exposure adds another variable to an already complex process. Several studies
        have examined the relationship between plastic chemical exposure and IVF
        outcomes.
      </p>
      <p>
        A landmark study from the Harvard T.H. Chan School of Public Health tracked
        256 women undergoing IVF cycles and measured urinary phthalate metabolites
        before and during treatment. Women in the highest quartile of di(2-ethylhexyl)
        phthalate (DEHP) exposure had 55% lower odds of clinical pregnancy and 56%
        lower odds of live birth compared to those in the lowest quartile. The
        researchers concluded that phthalate exposure may impair oocyte quality,
        embryo development, and implantation.
      </p>
      <p>
        BPA exposure has been associated with fewer oocytes retrieved during IVF, lower
        fertilization rates, reduced embryo quality scores, and thinner endometrial
        lining at the time of embryo transfer. A 2024 study found that women with BPA
        levels above the median had 34% fewer top-quality embryos available for
        transfer compared to those below the median.
      </p>
      <p>
        These findings suggest that reducing plastic chemical exposure before and
        during IVF treatment may improve outcomes. Several fertility clinics have
        begun recommending plastic reduction protocols as part of their pretreatment
        guidance, though this practice is not yet universal.
      </p>

      {/* Preconception detox plan */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Creating a Preconception Detox Plan
      </h2>
      <p>
        The ideal time to begin reducing microplastic exposure is 3 to 6 months before
        trying to conceive. This timeline allows the body to clear accumulated plastic
        chemicals (BPA has a half-life of approximately 6 hours in blood, but
        phthalates and PFAS persist longer) and ensures that a full cycle of sperm
        development (74 days) occurs under reduced-exposure conditions.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Diet Changes for Both Partners
      </h3>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Eliminate plastic food storage.</strong> Transfer all food to glass
          or stainless steel containers. Never heat food in plastic, including
          takeout containers.
        </li>
        <li>
          <strong>Filter your drinking water.</strong> Install a reverse osmosis
          system or use a high-quality carbon block filter. Avoid bottled water in
          plastic, which can contain hundreds of thousands of microplastic particles
          per bottle.
        </li>
        <li>
          <strong>Eat organic when possible.</strong> Organic produce has lower
          pesticide residues, and many pesticides are formulated with plastic
          adjuvants. Prioritize the &ldquo;Dirty Dozen&rdquo; (strawberries,
          spinach, kale, peaches, pears, nectarines, apples, grapes, bell peppers,
          cherries, blueberries, green beans) for organic purchases.
        </li>
        <li>
          <strong>Reduce processed food intake.</strong> Each step of food processing
          introduces potential plastic contamination from equipment and packaging.
          Cook from whole ingredients when possible.
        </li>
        <li>
          <strong>Choose seafood carefully.</strong> Opt for wild-caught fish over
          farmed, avoid shellfish consumed whole (mussels, oysters), and focus on
          species with lower microplastic accumulation such as salmon, sardines, and
          mackerel.
        </li>
        <li>
          <strong>Avoid canned foods.</strong> Choose foods in glass jars, frozen
          alternatives, or Tetra Pak packaging when fresh is not available.
        </li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Lifestyle Changes for Both Partners
      </h3>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Switch to natural-fiber clothing.</strong> Choose organic cotton,
          wool, linen, and hemp over polyester and nylon, especially for underwear
          and clothing worn closest to the body. Synthetic fabrics shed microplastic
          fibers that are absorbed through the skin.
        </li>
        <li>
          <strong>Replace plastic personal care products.</strong> Choose
          phthalate-free, fragrance-free products for both partners. Men&apos;s
          grooming products (aftershave, deodorant, shampoo) often contain high
          levels of phthalates.
        </li>
        <li>
          <strong>Improve indoor air quality.</strong> Vacuum with a HEPA filter
          regularly, ventilate your home daily, and consider a HEPA air purifier for
          the bedroom to reduce airborne microplastic fiber exposure during sleep.
        </li>
        <li>
          <strong>Avoid receipts and thermal paper.</strong> Thermal paper receipts
          are coated with BPA or BPS, which absorbs through the skin on contact. Use
          digital receipts when possible and avoid handling thermal paper.
        </li>
        <li>
          <strong>Do not dry clean clothing.</strong> Dry cleaning chemicals include
          perchloroethylene and other solvents carried in plastic-lined garment bags.
          If dry cleaning is necessary, remove the plastic bag and air out garments
          before wearing.
        </li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Male-Specific Recommendations
      </h3>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Avoid heat exposure to the groin area.</strong> Do not use laptops
          on your lap, avoid hot tubs and saunas, and wear loose-fitting cotton
          underwear. Heat combined with microplastic chemical exposure may have
          synergistic effects on sperm quality.
        </li>
        <li>
          <strong>Reduce plastic-packaged beverage consumption.</strong> Avoid
          drinking from plastic water bottles, especially those that have been
          exposed to heat or sunlight. Switch to glass or stainless steel.
        </li>
        <li>
          <strong>Minimize exposure to vinyl and PVC.</strong> Avoid vinyl shower
          curtains, PVC flooring, and plastic car interiors that off-gas phthalates,
          especially when heated. Use car window shades and ventilate your car before
          entering on hot days.
        </li>
      </ul>

      {/* Supplements */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Supplements That May Help Counteract Plastic Chemical Effects
      </h2>
      <p>
        While reducing exposure is the primary strategy, certain supplements have shown
        promise in mitigating the effects of plastic chemical exposure on fertility.
        Always consult your healthcare provider before starting any supplement regimen,
        especially when trying to conceive.
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>N-Acetyl Cysteine (NAC):</strong> A powerful antioxidant that
          increases glutathione levels, supporting the body&apos;s ability to
          detoxify plastic chemicals. A 2023 study found that NAC supplementation
          improved sperm parameters in men with high oxidative stress.
        </li>
        <li>
          <strong>Coenzyme Q10 (CoQ10):</strong> Supports mitochondrial function in
          both eggs and sperm. Studies have shown improved egg quality in IVF
          patients and improved sperm motility in men taking 200-600 mg daily.
        </li>
        <li>
          <strong>Folate (not folic acid):</strong> Methylfolate (the active form)
          supports DNA methylation, which may help counteract epigenetic changes
          caused by plastic chemicals. Critical for both egg and sperm quality.
        </li>
        <li>
          <strong>Vitamin D:</strong> Deficiency is linked to infertility in both
          sexes. Vitamin D also supports the body&apos;s detoxification pathways.
          Target blood levels of 40-60 ng/mL.
        </li>
        <li>
          <strong>Omega-3 fatty acids:</strong> DHA and EPA reduce inflammation
          caused by microplastic exposure. A 2024 study found that omega-3
          supplementation partially reversed the anti-androgenic effects of phthalate
          exposure in men.
        </li>
        <li>
          <strong>Selenium:</strong> An essential trace mineral that supports
          glutathione peroxidase activity, a key enzyme in detoxifying reactive
          oxygen species generated by plastic chemical exposure. Important for sperm
          quality.
        </li>
      </ul>

      {/* Preconception checklist */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Your Preconception Plastic Reduction Checklist
      </h2>
      <p>
        Use this checklist to systematically reduce your plastic exposure in the
        months before trying to conceive:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Replace all plastic food storage with glass or stainless steel</li>
        <li>Install a water filter (reverse osmosis or carbon block)</li>
        <li>Switch to stainless steel or glass water bottles</li>
        <li>Replace nonstick cookware with cast iron or stainless steel</li>
        <li>Switch to fragrance-free, phthalate-free personal care products (both partners)</li>
        <li>Replace polyester underwear and close-body clothing with organic cotton</li>
        <li>Audit cosmetics and skincare for microplastic ingredients</li>
        <li>Replace plastic cutting boards with wood or bamboo</li>
        <li>Switch from plastic tea bags to loose-leaf tea</li>
        <li>Reduce canned food consumption; choose glass jars or frozen alternatives</li>
        <li>Improve home air quality: HEPA vacuum, regular ventilation, air purifier</li>
        <li>Decline paper receipts or use digital alternatives</li>
        <li>Replace vinyl shower curtain with fabric alternative</li>
        <li>Begin fertility-supporting supplements (consult your doctor)</li>
        <li>Scan everyday products with the MicroPlastics app to identify hidden plastic ingredients</li>
      </ul>

      {/* FAQ Section */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Frequently Asked Questions
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        How long before trying to conceive should we start reducing plastic exposure?
      </h3>
      <p>
        Ideally, begin 3 to 6 months before trying to conceive. Sperm development
        takes approximately 74 days, so men need at least 3 months for a full cycle
        of sperm to develop under reduced-exposure conditions. For women, egg
        maturation takes about 90 days from recruitment to ovulation. Six months gives
        both partners the best chance of meaningful improvement in gamete quality.
        However, starting at any point is beneficial, as some plastic chemicals like
        BPA are cleared from the blood within hours of reducing exposure.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Can microplastic exposure cause infertility, or does it just reduce fertility?
      </h3>
      <p>
        Current evidence suggests that microplastic and plastic chemical exposure
        reduces fertility rather than causing absolute infertility. The effects are
        dose-dependent and cumulative, meaning that higher and longer exposures have
        greater impacts. For most people, plastic chemical exposure is one of many
        factors affecting fertility. Reducing exposure can improve your chances of
        conception, particularly when combined with other healthy lifestyle changes.
        However, if you have been trying to conceive for more than 12 months (or 6
        months if over 35), consult a reproductive endocrinologist regardless of
        plastic exposure.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Does reducing plastic exposure improve IVF success rates?
      </h3>
      <p>
        Several studies suggest it may. Research has shown that women with lower
        phthalate and BPA levels at the time of IVF treatment have higher
        fertilization rates, better embryo quality, and higher clinical pregnancy
        rates. While no randomized controlled trial has directly tested whether
        reducing plastic exposure before IVF improves outcomes, the observational
        evidence is strong enough that many fertility specialists now recommend
        plastic reduction as part of IVF preparation protocols.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Are organic foods important for fertility?
      </h3>
      <p>
        A 2018 study in <em>JAMA Internal Medicine</em> found that women who ate
        more organic produce had higher rates of clinical pregnancy and live birth
        during IVF treatment. While the study attributed this to lower pesticide
        exposure, many pesticide formulations contain plastic adjuvants and are
        packaged in plastic. Eating organic also tends to mean less processed food
        and less plastic packaging, indirectly reducing microplastic exposure.
        Prioritize organic for the produce you eat most frequently and for items on
        the &ldquo;Dirty Dozen&rdquo; list.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        My partner is skeptical about plastic reduction. How do I get them on board?
      </h3>
      <p>
        Share the specific research on testicular microplastics and sperm quality
        decline. The 2024 study finding microplastics in every testicular tissue
        sample tested, with concentrations correlating inversely with sperm count, is
        often compelling for skeptical partners. Emphasize that many changes are
        simple (switching from plastic to glass water bottles, for example) and
        benefit overall health beyond fertility. Start with the easiest changes first
        and build from there. The MicroPlastics app can help by making the invisible
        visible: scanning everyday products and seeing their microplastic risk scores
        can make the issue feel more concrete and actionable.
      </p>

      {/* App CTA */}
      <div className="bg-gradient-to-r from-[#1E6B47]/20 to-[#1E6B47]/5 rounded-xl p-6 mt-8">
        <h3 className="text-xl font-bold text-gray-800 mb-2">Check Your Products with the MicroPlastics App</h3>
        <p className="text-gray-600 mb-4">Scan any product to instantly see its microplastic risk level. Start with 3 free scans.</p>
        <AppStoreLink source="blog_post_inline" className="inline-flex items-center gap-2 bg-[#1E6B47] text-white font-semibold px-6 py-3 rounded-control hover:bg-[#f0a060] transition-colors pressable">Download Free on iOS</AppStoreLink>
      </div>

      <BlogWhatTheAppChecks
        items={[
          <>Baby/kid product material, glass, stainless, silicone, polypropylene, PPSU.</>,
          <>Packaging type, jar vs pouch vs multi-layer plastic.</>,
          <>Brand and product line, clean certifications flagged.</>,
          <>Use-context flags you log, sterilization heat, dishwasher cycles, age.</>,
          <>Cited published research behind each 0–100 score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan baby gear and pregnancy products before buying"
        sub="Bottles, sippy cups, baby food pouches, cosmetics. The app weighs material + brand + condition and suggests cleaner-packaged alternatives."
        buttonLabel="Scan baby gear in the app"
      />


      <BlogFAQ
        items={[
          {
            question: 'How long before trying to conceive should I reduce microplastic exposure?',
            answer:
              'At least 3 months, ideally 6. Sperm fully regenerate every 64-72 days, so changes the male partner makes today affect the sperm he produces 2-3 months later. Egg quality is also influenced by recent plasticiser exposure over the prior 3-6 months.',
          },
          {
            question: 'Does the male partner need to reduce exposure too?',
            answer:
              'Yes, and in some ways more than the female partner during preconception. BPA and phthalate exposure correlates strongly with reduced sperm count, motility, and morphology. Male reduction is often overlooked in fertility planning.',
          },
          {
            question: 'What are the top preconception microplastic-reduction priorities?',
            answer:
              'Same as during pregnancy, with one addition: switch from synthetic to cotton/wool underwear for the male partner (polyester releases microfibres directly onto reproductive skin). Then: filtered water in glass, no microwaving plastic, glass food storage, plastic-free personal care.',
          },
          {
            question: 'Can BPA levels actually drop measurably?',
            answer:
              'Yes. BPA has a short biological half-life (~6 hours) and urinary BPA drops rapidly when intake stops. The challenge is sustained avoidance, most people are re-exposed daily from food packaging, thermal receipts, and dust. With consistent reduction, meaningful sustained drops happen over 3 months.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Presented at the 41st Annual Meeting of ESHRE',
            year: 2025,
            title: 'Microplastics detected in human follicular and seminal fluid',
            publisher: 'European Society of Human Reproduction and Embryology',
            url: 'https://www.eurekalert.org/news-releases/1088676',
          },
          {
            authors: 'Garcia MA, Liu R, Nihart A, et al.',
            year: 2024,
            title: 'Quantitation and identification of microplastics in human placental specimens',
            publisher: 'Toxicological Sciences',
            url: 'https://academic.oup.com/toxsci/article/199/1/81/7609299',
          },
          {
            authors: 'Li D, Shi Y, Yang L, et al.',
            year: 2020,
            title: 'Microplastic release from polypropylene infant feeding bottles',
            publisher: 'Nature Food',
            url: 'https://www.nature.com/articles/s43016-020-00171-y',
          },
          {
            authors: 'Ragusa A, Notarstefano V, Svelato A, et al.',
            year: 2022,
            title: 'Raman Microspectroscopy Detection and Characterisation of Microplastics in Human Breastmilk',
            publisher: 'Polymers',
            url: 'https://www.mdpi.com/2073-4360/14/13/2700',
          },
          {
            authors: 'ACOG Committee Opinion 832',
            year: 2021,
            title: 'Reducing prenatal exposure to toxic environmental agents',
            publisher: 'American College of Obstetricians and Gynecologists',
            url: 'https://www.acog.org/clinical/clinical-guidance/committee-opinion/articles/2021/07/reducing-prenatal-exposure-to-toxic-environmental-agents',
          },
          {
            authors: 'Leslie HA, van Velzen MJM, Brandsma SH, et al.',
            year: 2022,
            title: 'Discovery and quantification of plastic particle pollution in human blood',
            publisher: 'Environment International',
            url: 'https://www.sciencedirect.com/science/article/pii/S0160412022001258',
          },
        ]}
      />


    </BlogPostLayout>
  )
}
